import React, { useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Form,
  Button,
  Row,
  Col,
  Table,
  Spinner,
  Alert,
  Card,
} from "react-bootstrap";
import { FaSearch, FaFilter, FaBoxOpen } from "react-icons/fa";
import { useGetProductReferencesQuery } from "../slices/productApiSlice";
import "../css/ProductReferenceScreen.css";

const ProductReferenceScreen = () => {
  const { category } = useParams();
  const [keyword, setKeyword] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const navigate = useNavigate();

  // ✅ Get user role (replace with Redux or Context if applicable)
  const { userInfo } = useSelector((state) => state.auth);
    const role = userInfo?.role;
  console.log(role);
  const { data, isLoading, isError, error, refetch, isFetching } =
    useGetProductReferencesQuery({
      keyword,
      category,
    });

  // Unique subcategories
  const subcategoryOptions = useMemo(() => {
    if (!data?.products) return [];
    const uniqueSubs = [...new Set(data.products.map((p) => p.subcategory))];
    return uniqueSubs.filter(Boolean).sort();
  }, [data]);

  // Filtered list
  const filteredProducts = useMemo(() => {
    if (!data?.products) return [];
    return subcategory
      ? data.products.filter((p) => p.subcategory === subcategory)
      : data.products;
  }, [data, subcategory]);

  const handleSubmit = (e) => {
    e.preventDefault();
    refetch();
  };

  // ✅ Restrict navigation based on role
  const goToProductDetail = useCallback(
    (id) => {
      if (role === "user") {
        navigate(`/product/${id}`);
      } else {
        navigate("/Home"); // redirect non-users
      }
    },
    [navigate, role]
  );

  return (
    <div className="p-2 p-md-3">
      {/* Back Button */}
      <Row className="mb-3">
        <Col xs="auto">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate("/Home")}
          >
            ← Back to Home
          </Button>
        </Col>
      </Row>

      {/* Header Card */}
      <Card className="shadow-sm mb-4 p-3 text-center rounded-3">
        <h5 className="mb-0" style={{ color: "#ff4d4d" }}>
          <FaBoxOpen className="me-2" />
          Products in <span style={{ color: "#1e90ff" }}>{category}</span>
        </h5>
      </Card>

      {/* Filter Form */}
      <Form onSubmit={handleSubmit} className="mb-3">
        <Row className="g-2 justify-content-center">
          <Col xs={12} sm={6} md={4}>
            <Form.Group controlId="keyword">
              <Form.Label style={{ fontSize: "0.9rem" }}>
                <FaSearch className="me-2 text-primary" />
                Search by Product Name
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter product name"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                className="shadow-sm"
              />
            </Form.Group>
          </Col>

          <Col xs={12} sm={6} md={4}>
            <Form.Group controlId="subcategory">
              <Form.Label style={{ fontSize: "0.9rem" }}>
                <FaFilter className="me-2 text-success" />
                Filter by Subcategory
              </Form.Label>
              <Form.Select
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                className="shadow-sm"
              >
                <option value="">All Subcategories</option>
                {subcategoryOptions.map((sub) => (
                  <option key={sub} value={sub}>
                    {sub}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col xs={12} md={2} className="d-grid mt-2 mt-md-0">
            <Button
              type="submit"
              size="sm"
              style={{
                background: "linear-gradient(90deg, #ff6a00, #ee0979)",
                color: "white",
                fontWeight: "600",
                borderRadius: "8px",
                fontSize: "0.9rem",
              }}
              disabled={isFetching}
            >
              {isFetching ? "Searching..." : "Search"}
            </Button>
          </Col>
        </Row>
      </Form>

      {/* Results */}
      {isLoading ? (
        <div className="text-center my-4">
          <Spinner animation="grow" variant="primary" />
          <div className="mt-2">Loading products...</div>
        </div>
      ) : isError ? (
        <Alert variant="danger" className="text-center">
          {error?.data?.message || "Failed to fetch products"}
        </Alert>
      ) : filteredProducts?.length > 0 ? (
        <>
          {/* Desktop/tablet view */}
          <div className="d-none d-md-block">
            <Table
              striped
              bordered
              hover
              responsive
              className="mt-3 shadow-sm"
              style={{
                borderRadius: "10px",
                overflow: "hidden",
                fontSize: "0.85rem",
              }}
            >
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Product Name</th>
                  <th>Subcategory</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, index) => (
                  <tr
                    key={product._id}
                    onClick={() => goToProductDetail(product._id)}
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? "#f7f7f7" : "#e0f7fa",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#d0ebff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        index % 2 === 0 ? "#f7f7f7" : "#e0f7fa")
                    }
                  >
                    <td>{index + 1}</td>
                    <td>
                      <FaBoxOpen className="me-2 text-warning" />
                      {product.name}
                    </td>
                    <td>{product.subcategory}</td>
                    <td>{product.price}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile-friendly card view */}
          <div className="d-block d-md-none">
            {filteredProducts.map((product, index) => (
              <Card
                key={product._id}
                className="mb-3 shadow-sm product-card"
                onClick={() => goToProductDetail(product._id)}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="product-header">
                      <strong>{index + 1}.</strong>{" "}
                      <FaBoxOpen className="me-1 text-warning" />
                      {product.name}
                    </div>
                    <div className={`badge subcategory-badge`}>
                      {product.subcategory}
                    </div>
                  </div>
                  <div className="price-tag mt-2">₹{product.price}</div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <Alert variant="info" className="text-center">
          No products found matching your filters.
        </Alert>
      )}
    </div>
  );
};

export default ProductReferenceScreen;
